/**
 * Uploads with REAL progress: `fetch` cannot report how much of a request
 * body went out, so every upload with a progress ring goes through
 * `XMLHttpRequest` and its `upload.onprogress`. Shared by the whole web (both
 * SPAs): the migration tool's PUTs, the presigned design uploads and the
 * multipart POSTs to the backend (`createXhrPostForm`, below).
 *
 * One presigned PUT of a file to the bucket (migration contract §10.1):
 *
 *   - the body is the port's lazy `Blob`: the browser streams it from disk,
 *     its bytes never reach this thread;
 *   - `Content-Type` is the one the presign signed; `Content-Length` is the
 *     Blob's own size (a browser does not let a script set it, and the
 *     signature requires the exact planned size — checked before, by the
 *     uploader);
 *   - it resolves with the HTTP status (2xx = done) and rejects only when
 *     aborted (`AbortError`); a network failure resolves with status 0, so the
 *     caller decides between a retry and a re-presign (`upload/rules.ts`
 *     `putVerdict`);
 *   - never the URL in a message or a log: it is a credential for an hour.
 *
 * A PART of a multipart upload (D66) goes the same way with `putPart`: its
 * body is the part's bytes (handed over by the fs worker) and it also
 * answers the `ETag` header, which completing the upload needs — the
 * bucket's CORS exposes it (measured on R2 dev, 2026-10-16). A part the
 * bucket refuses (a wrong length, a bad or expired signature) comes back as
 * status 0, not 403: R2's error answers carry no CORS headers, so the browser
 * hides them (same measurement) — the uploader retries with a fresh URL.
 *
 * A connection that stays open without sending a byte (mobile data «alive
 * but dead») never ends on its own until the system gives up, minutes later:
 * after `UPLOAD_STALL_MS` without progress while the body is still going out,
 * the request is dropped and answers status 0, so the caller retries it.
 *
 * `createXhrPut(() => new XMLHttpRequest())` in the browser; tests pass a fake.
 */

/** The slice of `XMLHttpRequest` used here. */
export interface XhrLike {
    open(method: string, url: string): void;
    setRequestHeader(name: string, value: string): void;
    send(body: Blob | Uint8Array<ArrayBuffer> | FormData): void;
    getResponseHeader?(name: string): string | null;
    abort(): void;
    readonly status: number;
    onload: (() => void) | null;
    onerror: (() => void) | null;
    onabort: (() => void) | null;
    ontimeout: (() => void) | null;
    readonly upload: {
        onprogress: ((event: { loaded: number; total: number; lengthComputable: boolean }) => void) | null;
        /** The body is all out (the answer may still take a while). */
        onload?: (() => void) | null;
    };
}

export interface PutRequest {
    readonly url: string;
    readonly body: Blob | Uint8Array<ArrayBuffer>;
    readonly contentType: string;
    readonly signal?: AbortSignal;
    /** Bytes sent so far (never more than the body's size). */
    readonly onProgress?: (loaded: number) => void;
}

/** `(request) → HTTP status` (0: no answer). Rejects only with an `AbortError`. */
export type PutFile = (request: PutRequest) => Promise<number>;

/** One part's PUT: its status and the `ETag` the bucket answered (null when none could be read). */
export interface PartAnswer {
    readonly status: number;
    readonly etag: string | null;
}

/** `(request) → status + ETag`. Rejects only with an `AbortError`. */
export type PutPart = (request: PutRequest) => Promise<PartAnswer>;

/** Without a single byte of the body going out for this long, the connection
 *  is taken as dead: status 0, which the caller retries. Only while SENDING —
 *  once the body is out (`upload.onload`) the server may take its time. */
export const UPLOAD_STALL_MS = 60_000;

/** A timer that fires `onStall` unless `poke`d again within `stallMs`. */
function stallWatch(stallMs: number, onStall: () => void): { poke(): void; stop(): void } {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const stop = () => {
        if (timer !== null) clearTimeout(timer);
        timer = null;
    };
    const poke = () => {
        stop();
        timer = setTimeout(onStall, stallMs);
    };
    return { poke, stop };
}

function sizeOf(body: Blob | Uint8Array): number {
    return body instanceof Uint8Array ? body.byteLength : body.size;
}

function abortError(): Error {
    const error = new Error('upload aborted');
    error.name = 'AbortError';
    return error;
}

export function createXhrPut(makeXhr: () => XhrLike): PutFile {
    const send = createXhrPutPart(makeXhr);
    return async (request) => (await send(request)).status;
}

export function createXhrPutPart(makeXhr: () => XhrLike, stallMs = UPLOAD_STALL_MS): PutPart {
    return (request) =>
        new Promise<PartAnswer>((resolve, reject) => {
            if (request.signal?.aborted) {
                reject(abortError());
                return;
            }
            const xhr = makeXhr();
            let settled = false;
            const finish = (fn: () => void) => {
                if (settled) return;
                settled = true;
                stall.stop();
                request.signal?.removeEventListener('abort', onAbort);
                fn();
            };
            const stall = stallWatch(stallMs, () => {
                finish(() => resolve({ status: 0, etag: null }));
                xhr.abort();
            });
            const onAbort = () => {
                try {
                    xhr.abort();
                } finally {
                    finish(() => reject(abortError()));
                }
            };
            request.signal?.addEventListener('abort', onAbort, { once: true });
            xhr.upload.onprogress = (event) => {
                if (settled) return;
                stall.poke();
                request.onProgress?.(Math.min(event.loaded, sizeOf(request.body)));
            };
            xhr.upload.onload = () => stall.stop();
            xhr.onload = () =>
                finish(() => {
                    let etag: string | null = null;
                    try {
                        etag = xhr.getResponseHeader?.('ETag') ?? null;
                    } catch {
                        etag = null;
                    }
                    resolve({ status: xhr.status, etag });
                });
            xhr.onerror = () => finish(() => resolve({ status: 0, etag: null }));
            xhr.ontimeout = () => finish(() => resolve({ status: 0, etag: null }));
            xhr.onabort = () => finish(() => reject(abortError()));
            try {
                xhr.open('PUT', request.url);
                // A part has no signed type (the upload's was fixed when it was opened): none is sent.
                if (request.contentType !== '') xhr.setRequestHeader('Content-Type', request.contentType);
                xhr.send(request.body);
                if (!settled) stall.poke();
            } catch {
                finish(() => resolve({ status: 0, etag: null }));
            }
        });
}

/** The browser's PUT. */
export function browserPut(): PutFile {
    return createXhrPut(() => new XMLHttpRequest() as unknown as XhrLike);
}

/** The browser's PUT of one part (D66). */
export function browserPutPart(): PutPart {
    return createXhrPutPart(() => new XMLHttpRequest() as unknown as XhrLike);
}

// ── A multipart POST to our own backend, with progress ──────────────────────

export interface FormPostRequest {
    readonly url: string;
    readonly body: FormData;
    /** Request headers (the tenant's `Authorization: Bearer …`). */
    readonly headers?: Readonly<Record<string, string>>;
    /** Send the session cookie (the admin's auth). */
    readonly withCredentials?: boolean;
    readonly signal?: AbortSignal;
    /** Bytes of the request body sent so far, and its total. */
    readonly onProgress?: (loaded: number, total: number) => void;
}

/** The HTTP status (0 = no answer) and the response body as text. */
export interface FormPostAnswer {
    readonly status: number;
    readonly text: string;
}

export type PostForm = (request: FormPostRequest) => Promise<FormPostAnswer>;

/** The slice of `XMLHttpRequest` a POST also needs. */
export interface XhrPostLike extends XhrLike {
    withCredentials: boolean;
    readonly responseText: string;
}

/** `(request) → status + body`. Rejects only with an `AbortError`; a network
 *  failure resolves with status 0, so the caller decides whether to retry. */
export function createXhrPostForm(makeXhr: () => XhrPostLike, stallMs = UPLOAD_STALL_MS): PostForm {
    return (request) =>
        new Promise<FormPostAnswer>((resolve, reject) => {
            if (request.signal?.aborted) {
                reject(abortError());
                return;
            }
            const xhr = makeXhr();
            let settled = false;
            const finish = (fn: () => void) => {
                if (settled) return;
                settled = true;
                stall.stop();
                request.signal?.removeEventListener('abort', onAbort);
                fn();
            };
            const stall = stallWatch(stallMs, () => {
                finish(() => resolve({ status: 0, text: '' }));
                xhr.abort();
            });
            const onAbort = () => {
                try {
                    xhr.abort();
                } finally {
                    finish(() => reject(abortError()));
                }
            };
            request.signal?.addEventListener('abort', onAbort, { once: true });
            xhr.upload.onprogress = (event) => {
                if (settled) return;
                stall.poke();
                if (!event.lengthComputable) return;
                request.onProgress?.(event.loaded, event.total);
            };
            xhr.upload.onload = () => stall.stop();
            xhr.onload = () => finish(() => {
                let text = '';
                try {
                    text = xhr.responseText ?? '';
                } catch {
                    text = '';
                }
                resolve({ status: xhr.status, text });
            });
            xhr.onerror = () => finish(() => resolve({ status: 0, text: '' }));
            xhr.ontimeout = () => finish(() => resolve({ status: 0, text: '' }));
            xhr.onabort = () => finish(() => reject(abortError()));
            try {
                xhr.open('POST', request.url);
                xhr.withCredentials = request.withCredentials === true;
                for (const [name, value] of Object.entries(request.headers ?? {})) xhr.setRequestHeader(name, value);
                xhr.send(request.body);
                if (!settled) stall.poke();
            } catch {
                finish(() => resolve({ status: 0, text: '' }));
            }
        });
}

/** The browser's multipart POST with progress. */
export function browserPostForm(): PostForm {
    return createXhrPostForm(() => new XMLHttpRequest() as unknown as XhrPostLike);
}
